/**
 * Denge Arenası "düşene kadar tekrar" kuralları. İstemci soru sırasını üretir; sunucu aynı kuralla doğrular.
 * 1. tur öğretmenin sırasıdır (1..N). Sonraki her tur 1..N'nin karışık bir sırasıdır ve bir önceki
 * turun son sorusuyla başlamaz. Son tur (öğrenci düşünce veya süre dolunca) eksik kalabilir.
 */
export const BALANCE_ENDLESS = {
  /** Düşmeden bu süreye ulaşan öğrenci oyunu başarıyla bitirir. */
  maxSurvivalMs: 5 * 60_000,
  /** Kayıt büyüklüğü için teknik üst sınır; 5 dakikada normalde ulaşılmaz. */
  maxAnswers: 400,
  /** Her yeni turun başında rüzgâr gücü bu oranla çarpılır. */
  windGrowthPerRound: 1.03,
} as const;

export type BalanceSequenceEntry = { level: number; round?: number };

/** Yeni tur için karışık soru sırası (1..N). `random` [0,1) üretir; ilk soru önceki turun son sorusu olmaz. */
export function shuffledBalanceRound(questionCount: number, previousLast: number | null, random: () => number): number[] {
  const order = Array.from({ length: questionCount }, (_, index) => index + 1);
  for (let index = order.length - 1; index > 0; index -= 1) {
    const swap = Math.floor(random() * (index + 1));
    [order[index], order[swap]] = [order[swap], order[index]];
  }
  if (order.length > 1 && order[0] === previousLast) [order[0], order[1]] = [order[1], order[0]];
  return order;
}

/** Cevap dizisinin tur/soru yapısını doğrular. Tekrar kapalıyken yalnız 1..k sırası ve tur 1 kabul edilir. */
export function isValidBalanceSequence(entries: readonly BalanceSequenceEntry[], questionCount: number, repeatUntilFall: boolean): boolean {
  if (!repeatUntilFall) return entries.every((entry, index) => entry.level === index + 1 && (entry.round ?? 1) === 1);
  if (entries.length > BALANCE_ENDLESS.maxAnswers) return false;
  let previousLast: number | null = null;
  for (let start = 0, round = 1; start < entries.length; start += questionCount, round += 1) {
    const slice = entries.slice(start, start + questionCount);
    const seen = new Set<number>();
    for (const [offset, entry] of slice.entries()) {
      if ((entry.round ?? 1) !== round || entry.level < 1 || entry.level > questionCount || seen.has(entry.level)) return false;
      if (round === 1 && entry.level !== offset + 1) return false;
      seen.add(entry.level);
    }
    if (round > 1 && questionCount > 1 && slice[0].level === previousLast) return false;
    previousLast = slice.at(-1)?.level ?? null;
  }
  return true;
}

/**
 * Tekrar modunda sonuç kartı ve ilk 10 durum metni. "Tamamlanamadı" yalnız ilk tur bitmeden
 * (bütün sorular cevaplanmadan) düşüldüyse kullanılır.
 */
export function balanceRepeatStatus(input: { completed: boolean; answered: number; fellAtQuestion: number | null; questionCount: number }): string {
  if (input.completed) return '5 dakika düşmedi!';
  if (input.answered < input.questionCount) return `Tamamlanamadı · ${input.answered}/${input.questionCount} soru cevaplandı`;
  const fellAt = input.fellAtQuestion ?? input.answered;
  return `Bütün soruları gördü · ${Math.max(1, Math.ceil(fellAt / input.questionCount))}. turda düştü`;
}
